// src/pages/RegisterPage.jsx

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUser, fetchCurrentUser } from "../userSlice";
import { register } from "../services/authService";
import { acceptInvitation } from "../services/invitationService";
import { createJoinRequest } from "../services/joinRequestService";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert
} from "@mui/material";
import { useSnackbar } from "notistack";
import AuthHeader from "../components/AuthHeader";

function RegisterPage() {
  // short English comments only
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteToken, setInviteToken] = useState("");
  const [orgId, setOrgId] = useState("");
  const [orgName, setOrgName] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("inviteToken");
    const e = params.get("inviteEmail");
    const orgIdParam = params.get("orgId");
    const orgNameParam = params.get("orgName");
    
    if (t) setInviteToken(t);
    if (e) setEmail(e);
    if (orgIdParam) setOrgId(orgIdParam);
    if (orgNameParam) setOrgName(decodeURIComponent(orgNameParam));
  }, [location.search]);

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      const data = await register(username, email, password, inviteToken);
      dispatch(setUser(data.user));
      dispatch(fetchCurrentUser());
      enqueueSnackbar(data.message || "Registered successfully", { variant: "success" });

      // if there's an inviteToken => accept and go to rooms
      if (inviteToken) {
        try {
          await acceptInvitation(inviteToken);
          enqueueSnackbar("Invitation accepted", { variant: "success" });
          navigate("/rooms");
        } catch (err) {
          enqueueSnackbar("Failed to accept invitation", { variant: "error" });
          navigate("/rooms"); // Still go to rooms even if invitation acceptance fails
        }
      } else if (orgId) {
        // Organization invitation link - create join request
        try {
          await createJoinRequest(orgId, `Joined via invitation link for ${orgName}`);
          enqueueSnackbar("Join request submitted successfully! Please wait for admin approval.", { variant: "success" });
          navigate("/join-pending", { state: { organizationName: orgName } });
        } catch (err) {
          console.error("Failed to create join request:", err);
          enqueueSnackbar("Registration successful, but failed to submit join request. Please try joining manually.", { variant: "warning" });
          navigate("/organization-choice");
        }
      } else {
        // No invite token and no org context - redirect to organization choice
        navigate("/organization-choice");
      }
    } catch (err) {
      if (err.response && err.response.data?.error) {
        enqueueSnackbar(err.response.data.error, { variant: "error" });
      } else {
        enqueueSnackbar("Registration failed", { variant: "error" });
      }
    }
  }

  return (
    <>
      <AuthHeader />
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }} elevation={2}>
          <Typography variant="h5" gutterBottom>
            Register
          </Typography>
        
        {orgName && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You're registering to join <strong>{orgName}</strong>. 
            After registration, your join request will be submitted for admin approval.
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
        >
          <TextField
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            fullWidth
          />

          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            InputProps={{ readOnly: inviteToken ? true : false }}
          />

          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
          />

          <Button variant="contained" type="submit">
            Register
          </Button>
        </Box>
      </Paper>
    </Container>
    </>
  );
}

export default RegisterPage;
